import React from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Snackbar from '@quintoandar/cozy-core/Snackbar';

import ROUTES, { routesNames } from 'routes/constants';
import PageWrapper from 'components/PageWrapper';
import NotStartedPhotoSessions from 'containers/PhotoSessions/NotStartedPhotoSessions';
import ReschedulePhotoSessions from 'containers/PhotoSessions/ReschedulePhotoSessions';
import StartedPhotoSessions from 'containers/PhotoSessions/StartedPhotoSessions';
import ErrorBoundary from 'components/ErrorBoundary';
import { logUncaught } from 'utils/log';

import {
  makeSelectShowDetailError,
  makeSelectShowNotStartedListError,
  makeSelectShowStartedListError,
} from './selectors';

import messages from './messages';
import {
  photoSessionsListPageViewedAnalytics,
  fetchNotStartedPhotoSessionsAction,
  fetchStartedPhotoSessionsAction,
} from './actions';

export class Tabs extends React.PureComponent {
  componentDidMount() {
    this.props.trackPageView();
  }

  getTabIndex = (route, showRescheduleTab) => {
    if (route.name === routesNames.notStartedPhotoSessions) {
      return 0;
    }

    if (route.name === routesNames.reschedulePhotoSessions) {
      return showRescheduleTab ? 1 : 0;
    }

    return showRescheduleTab ? 2 : 1;
  };

  getSnackbarProps = () => {
    const {
      intl,
      showDetailError,
      showStartedListError,
      showNotStartedListError,
      route,
      showRescheduleTab,
      fetchStartedPhotoSessions,
      fetchNotStartedPhotoSessions,
    } = this.props;

    const tabIndex = this.getTabIndex(route, showRescheduleTab);
    const startedTabIndex = showRescheduleTab ? 2 : 1;
    const notStartedTabIndex = 0;
    const rescheduleTabIndex = 1;

    if (showDetailError) {
      return {
        color: 'error',
        message: intl.formatMessage(messages.photoSessionDetailError),
        open: true,
      };
    }

    if (showStartedListError && tabIndex === startedTabIndex) {
      return {
        color: 'error',
        open: true,
        message: intl.formatMessage(messages.startedPhotoSessionsError),
        buttonLabel: intl.formatMessage(messages.tryAgain),
        onClick: fetchStartedPhotoSessions,
      };
    }

    const isNotStartedOrRescheduleTab = tabIndex === notStartedTabIndex
      || (showRescheduleTab && tabIndex === rescheduleTabIndex);

    if (showNotStartedListError && isNotStartedOrRescheduleTab) {
      return {
        color: 'error',
        open: true,
        message: intl.formatMessage(messages.notStartedPhotoSessionsError),
        buttonLabel: intl.formatMessage(messages.tryAgain),
        onClick: fetchNotStartedPhotoSessions,
      };
    }

    return null;
  }

  getUrl = (index, showRescheduleTab) => {
    if (index === 0) {
      return ROUTES.NOT_STARTED_PHOTO_SESSIONS;
    }

    if (showRescheduleTab) {
      if (index === 1) {
        return ROUTES.RESCHEDULE_PHOTO_SESSIONS;
      }
      return ROUTES.STARTED_PHOTO_SESSIONS;
    }

    return ROUTES.STARTED_PHOTO_SESSIONS;
  };

  handleIndexChange = (index) => {
    const url = this.getUrl(index, this.props.showRescheduleTab);
    this.props.onTabChange(url);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  handleSwipeableViewsChange = (index) => this.handleIndexChange(index);

  render() {
    const { route, showRescheduleTab } = this.props;
    const tabIndex = this.getTabIndex(route, showRescheduleTab);
    const snackbarProps = this.getSnackbarProps();

    // SwipeableViews counts null children. A hidden Reagendar slot would make
    // Pendentes render the empty panel, then animate through it once inserted.
    const slides = [
      <NotStartedPhotoSessions key="not-started" />,
      showRescheduleTab ? <ReschedulePhotoSessions key="reschedule" /> : null,
      <StartedPhotoSessions key="started" />,
    ].filter(Boolean);

    return (
      <PageWrapper>
        <SwipeableViews
          index={tabIndex}
          onChangeIndex={this.handleSwipeableViewsChange}
          enableMouseEvents
        >
          {slides}
        </SwipeableViews>

        {snackbarProps && (
          <ErrorBoundary errorComponent={null} logError={logUncaught}>
            <Snackbar {...snackbarProps} />
          </ErrorBoundary>
        )}
      </PageWrapper>
    );
  }
}

Tabs.propTypes = {
  intl: PropTypes.object,
  route: PropTypes.shape({
    name: PropTypes.string,
  }),
  showRescheduleTab: PropTypes.bool,
  onTabChange: PropTypes.func,
  trackPageView: PropTypes.func,
  showDetailError: PropTypes.bool,
  showStartedListError: PropTypes.bool,
  showNotStartedListError: PropTypes.bool,
  fetchStartedPhotoSessions: PropTypes.func.isRequired,
  fetchNotStartedPhotoSessions: PropTypes.func.isRequired,
};

Tabs.defaultProps = {
  showRescheduleTab: false,
};

const mapStateToProps = createStructuredSelector({
  showDetailError: makeSelectShowDetailError(),
  showNotStartedListError: makeSelectShowNotStartedListError(),
  showStartedListError: makeSelectShowStartedListError(),
});

export const mapDispatchToProps = (dispatch) => ({
  onTabChange: (url) => dispatch(push(url)),
  trackPageView: () => dispatch(photoSessionsListPageViewedAnalytics()),
  fetchStartedPhotoSessions: () => dispatch(fetchStartedPhotoSessionsAction()),
  fetchNotStartedPhotoSessions: () => dispatch(fetchNotStartedPhotoSessionsAction()),
});

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(Tabs);
